/**
 * Edit profile modal
 */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import { INft, Network, enums } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import Image from '../../../atoms/Image';
import Button from '../../../molecules/Button';
import { IStore } from '../../../reducers';
import { networkSelector } from '../../../selectors/network';
import InfoIcon from '../../../molecules/Notification/images/info.svg';
import { KattanaAPI, UserAPI } from '../../../store/createStore';
import Loader from '../../../molecules/Table/Loader';
import { actionRefreshCompetition } from '../../../actions/competition';
import { actionCreateNotification } from '../../../actions/notifications';
import NftIcon from '../../../molecules/NftIcon/NftIcon';
import { AVATAR_PLACEHOLDER } from '../../../helpers/constants';

import './styles.scss';

interface IChangeAvatarModalProps {
  network: 'EVM' | 'ELROND' | 'TRON' | 'TON';
  authToken: string;
  onCancel: () => void;
}

const ChangeAvatarModal = ({
  network,
  authToken,
  onCancel,
}: IChangeAvatarModalProps) => {
  const currentNetwork = useSelector<IStore, Network>(networkSelector);
  const plan = useSelector((state: IStore) => state.features.currentPlan);
  const { address } = useAccount();

  const [nfts, setNfts] = useState<INft[]>([]);
  const [selected, setSelected] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const controller = new AbortController();
    if (address) {
      KattanaAPI.nfts(currentNetwork.chain, address, controller).then(
        (data) => {
          if (data) {
            setNfts(data);
          }
          setLoading(false);
        }
      );
    }

    return () => controller.abort();
  }, [address, currentNetwork.chain]);

  const handleAvatarChange = async (imgUrl: string) => {
    if (address) {
      setSubmitLoading(true);
      const data = await UserAPI.updateUser(address, network, authToken, {
        img_url: imgUrl,
      });
      setSubmitLoading(false);
      if (data?.status === 'User updated') {
        dispatch(actionRefreshCompetition());
      } else {
        dispatch(
          actionCreateNotification({
            kind: 'error',
            message: 'Something went wrong...',
          })
        );
      }
      onCancel();
    }
  };

  return (
    <>
      <div className='edit_profile_modal__backdrop' onClick={onCancel} />
      <div className='edit_profile_modal no_scroll'>
        <div className='edit_profile_modal__header'>
          <div className='edit_profile_modal__header__title'>
            <Text size={16}>Select NFT as avatar</Text>
            <Image size='l' src={currentNetwork.getImage()} />
            <Text size={14}>{currentNetwork.getTitle()}</Text>
          </div>
          <div
            className='edit_profile_modal__header__close icon-close'
            onClick={onCancel}
          />
        </div>
        <div className='edit_profile_modal__body'>
          {plan !== enums.PricingPlan.premium && (
            <div className='notification notification--info'>
              <img
                className='notification__icon'
                src={InfoIcon}
                alt='info icon'
              />
              <div
                className='notification__text'
                title='Available only for premium subscription!'
              >
                Available only for premium subscription!
              </div>
            </div>
          )}
          {loading ? (
            <div className='centrify'>
              <Loader hide={!loading} />
            </div>
          ) : !nfts.length ? (
            <div className='centrify text--grey-200'>No data available</div>
          ) : (
            <div className='edit_profile_modal__body__list'>
              {nfts.map((nft) => (
                <NftIcon
                  key={nft.contract_address}
                  nft={nft}
                  selected={selected}
                  onSelect={setSelected}
                />
              ))}
            </div>
          )}
        </div>
        <div className='edit_profile_modal__btn'>
          <Button
            loading={submitLoading}
            size='m'
            variant='secondary'
            action={() => handleAvatarChange(AVATAR_PLACEHOLDER)}
            text='Use Default'
          />
          <Button
            loading={submitLoading}
            size='m'
            variant='primary'
            action={() => handleAvatarChange(selected!)}
            disabled={!selected || plan !== enums.PricingPlan.premium}
            text='Select'
          />
        </div>
      </div>
    </>
  );
};

export default ChangeAvatarModal;
